import React from "react";
import { Card, Typography, Select, Space, DatePicker, Input, Grid } from "antd";
import {
  IdcardOutlined,
  DownOutlined,
  UserOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import type { OrderType } from "../model/types";
import { MobileDatePicker } from "../../../shared/components/MobileDatePicker";

const { Text } = Typography;

type OptionType = {
  value: number | string;
  label: string;
};

type Props = {
  customer?: OptionType | null;
  product?: OptionType | null;
  staffId?: number | string;
  salesDate?: Dayjs | null;
  onChangeCustomer?: (v: OptionType | null) => void;
  onChangeProduct?: (v: OptionType | null) => void;
  onChangeStaff?: (v: number | string) => void;
  onChangeDate?: (date: Dayjs | null) => void;
  customerOptions?: OptionType[];
  productOptions?: OptionType[];
  staffOptions?: OptionType[];
  mode?: "CREATE_SINGLE" | "CREATE_MULTI" | "EDIT" | "COPY" | "VIEW";
  orderType: OrderType | "";
  onSearchCustomer?: (search: string) => void;
  customerLoading?: boolean;
  onSearchProduct?: (search: string) => void;
  productLoading?: boolean;
  companyName?: any;
  readOnly?: boolean;
  sourceOrder?: string;
  deliveryDateDisabled?: boolean;
  minimumDeliveryDate?: string | null;
  deliveryDateError?: string;
};

const BasicInfoCard: React.FC<Props> = ({
  customer,
  product,
  staffId,
  salesDate,
  onChangeCustomer,
  onChangeProduct,
  onChangeStaff,
  onChangeDate,
  customerOptions = [],
  productOptions = [],
  staffOptions = [],
  mode,
  orderType,
  onSearchCustomer,
  customerLoading,
  onSearchProduct,
  productLoading,
  companyName,
  readOnly = false,
  sourceOrder,
  deliveryDateDisabled = false,
  minimumDeliveryDate,
  deliveryDateError,
}) => {
  const [customStaff, setCustomStaff] = React.useState<string>("");
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const dynamicStaffOptions = React.useMemo(() => {
    if (!customStaff) return staffOptions;
    if (
      staffOptions.some(
        (opt) =>
          typeof opt.label === "string" && opt.label.includes(customStaff),
      )
    ) {
      return staffOptions;
    }
    return [...staffOptions, { value: customStaff, label: `${customStaff}` }];
  }, [staffOptions, customStaff]);
  const selectedStaffLabel = React.useMemo(
    () =>
      dynamicStaffOptions.find((option) => option.value === staffId)?.label ||
      "-",
    [dynamicStaffOptions, staffId],
  );

  return (
    <Card
      className="basic-info-card"
      style={{
        borderRadius: 16,
        background: "#fff",
        border: "1px solid #e5e7eb",
      }}
    >
      <Space style={{ marginBottom: 16 }}>
        <div
          style={{
            background: "#22c55e",
            borderRadius: 6,
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IdcardOutlined style={{ color: "#fff", fontSize: 14 }} />
        </div>

        <Text strong style={{ fontSize: 16 }}>
          基本情報
        </Text>
      </Space>

      <div className="customer-company-row">
        <div className="customer-field">
          <Text style={{ color: "#6b7280", fontSize: 13 }}>お客様</Text>

          {readOnly ? (
            <Input
              style={{ width: "100%", marginTop: 6 }}
              value={customer?.label}
              readOnly
            />
          ) : (
            <Select
              style={{ width: "100%", marginTop: 6 }}
              showSearch
              placeholder="株式会社 新鮮青果"
              labelInValue
              value={customer || undefined}
              options={customerOptions}
              onChange={(val) => onChangeCustomer?.(val)}
              onSearch={onSearchCustomer}
              filterOption={false}
              loading={customerLoading}
              allowClear
              disabled={readOnly}
              suffixIcon={
                <span style={{ color: "#9ca3af" }}>
                  <DownOutlined />
                </span>
              }
              notFoundContent="該当データなし"
            />
          )}
        </div>

        <div className="company-name-field">
          <Text style={{ color: "#6b7280", fontSize: 13 }}>会社名</Text>
          <div className="company-name-display">
            {(companyName?.company_out_name || "").trim()}
          </div>
        </div>
      </div>

      {(orderType === "SINGLE" || mode === "CREATE_SINGLE") && (
        <div style={{ marginBottom: 16, width: "100%" }}>
          <Text style={{ color: "#6b7280", fontSize: 13 }}>商品名</Text>
          <Select
            style={{ width: "100%", marginTop: 6 }}
            showSearch
            placeholder="商品名を検索..."
            labelInValue
            value={product || undefined}
            options={productOptions}
            onChange={(val) => onChangeProduct?.(val)}
            onSearch={onSearchProduct}
            filterOption={false}
            loading={productLoading}
            allowClear
            disabled={readOnly}
            suffixIcon={
              <span style={{ color: "#9ca3af" }}>
                <DownOutlined />
              </span>
            }
            notFoundContent="該当データなし"
          />
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <Text style={{ color: "#6b7280", fontSize: 13 }}>ご担当者名</Text>

        {readOnly ? (
          <Input
            style={{ width: "100%", marginTop: 6 }}
            value={selectedStaffLabel}
            readOnly
          />
        ) : (
          <Select
            style={{ width: "100%", marginTop: 6 }}
            showSearch
            placeholder="担当者を選択…"
            value={staffId}
            options={dynamicStaffOptions}
            onChange={onChangeStaff}
            onSearch={setCustomStaff}
            onBlur={() => setCustomStaff("")}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            suffixIcon={<UserOutlined style={{ color: "#9ca3af" }} />}
            notFoundContent="該当データなし"
            allowClear
          />
        )}
      </div>

      <div>
        <Text style={{ color: "#6b7280", fontSize: 13 }}>出荷日</Text>

        {readOnly ? (
          <Input
            style={{ width: "100%", marginTop: 6 }}
            value={salesDate?.format("YYYY/MM/DD") || ""}
            readOnly
          />
        ) : (
          isMobile ? (
            <MobileDatePicker
              value={salesDate}
              onChange={onChangeDate}
              status={deliveryDateError ? "error" : undefined}
              disabledDate={(current) => {
                const minimum = minimumDeliveryDate
                  ? dayjs(minimumDeliveryDate).startOf("day")
                  : dayjs().startOf("day");
                return current.isBefore(minimum, "day");
              }}
              placeholder="日付を選択"
              disabled={deliveryDateDisabled}
            />
          ) : (
            <DatePicker
              style={{ width: "100%", marginTop: 6 }}
              value={salesDate}
              onChange={onChangeDate}
              format="YYYY/MM/DD"
              status={deliveryDateError ? "error" : undefined}
              disabledDate={(current) => {
                const minimum = minimumDeliveryDate
                  ? dayjs(minimumDeliveryDate).startOf("day")
                  : dayjs().startOf("day");
                return Boolean(current && current < minimum);
              }}
              placeholder="日付を選択"
              suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
              disabled={deliveryDateDisabled}
            />
          )
        )}
        {deliveryDateError ? (
          <Text type="danger" className="lead-time-field-error">
            {deliveryDateError}
          </Text>
        ) : deliveryDateDisabled && !readOnly ? (
          <Text type="secondary" className="lead-time-field-help">
            お客様と商品を選択すると出荷日を選択できます。
          </Text>
        ) : null}
      </div>
    </Card>
  );
};

export default BasicInfoCard;
